// Phase 36 Batch AS — Waitlist email-capture modal.
//
// Used by the new homepage pricing cards. POSTs to /api/waitlist.
// Stateless: open/close handled by parent, success state self-managed
// so the user sees a confirmation before the modal dismisses.

import { useMemo, useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Users } from "lucide-react";
import {
  trackWaitlistSignup,
  trackWaitlistSignupWithReferral,
  trackWaitlistSignupSuccess,
} from "@/lib/landingEvents";
import WaitlistSuccess from "@/components/landing/WaitlistSuccess";

const PLAN_LABEL = {
  pro_monthly: "Pro (monthly)",
  pro_yearly: "Pro (annual)",
  founders_lifetime: "Founders Lifetime",
};

// Batch AT — sessionStorage key for the captured referral code. Kept
// in session (NOT local) so a fresh browser visit without ?ref= drops
// any stale attribution. Synced with `Landing.jsx`.
const REF_STORAGE_KEY = "runidea_ref_code";

// Reads ?ref=<code> from the current URL, normalises to lowercase, and
// persists it to sessionStorage so multi-tab navigation between landing
// and modal still keeps the attribution. If no ?ref= is present we fall
// back to whatever was previously persisted in this tab.
function _readReferralCode() {
  if (typeof window === "undefined") return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const fromUrl = (params.get("ref") || "").trim().toLowerCase().slice(0, 120);
    if (fromUrl) {
      try {
        window.sessionStorage?.setItem(REF_STORAGE_KEY, fromUrl);
      } catch { /* private mode */ }
      return fromUrl;
    }
    const stored = window.sessionStorage?.getItem(REF_STORAGE_KEY);
    return stored ? stored.trim().toLowerCase().slice(0, 120) : null;
  } catch {
    return null;
  }
}

export default function WaitlistModal({ open, onOpenChange, plan }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  // Batch AU — the signup response (includes the user's own
  // referral_code) is held so the success screen can render the
  // share link immediately, without a second round-trip.
  const [signup, setSignup] = useState(null);

  // Recompute the referral code each time the modal opens (so users
  // who visit twice in the same tab pick up the freshest ?ref=). Using
  // useMemo instead of useEffect+setState satisfies the
  // react-hooks/set-state-in-effect rule.
  const referral = useMemo(() => (open ? _readReferralCode() : null), [open]);

  const reset = () => {
    setEmail("");
    setName("");
    setDone(false);
    setErr("");
    setSignup(null);
  };

  const handleClose = (v) => {
    onOpenChange?.(v);
    if (!v) setTimeout(reset, 250);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setErr("Email is required");
      return;
    }
    setSubmitting(true);
    setErr("");
    try {
      const payload = {
        email: email.trim().toLowerCase(),
        plan,
        name: name.trim() || null,
      };
      if (referral) payload.referral_source = referral;
      const res = await api.post("/waitlist", payload);
      const row = res?.data || {};
      setSignup(row);
      trackWaitlistSignup({ plan });
      trackWaitlistSignupSuccess({ plan, referral_code: row.referral_code });
      if (referral) {
        trackWaitlistSignupWithReferral({ plan, referral_source: referral });
      }
      setDone(true);
    } catch (e2) {
      const detail = e2?.response?.data?.detail;
      if (detail?.error === "founders_full") {
        setErr("All founders spots are reserved. Try the Pro plan instead.");
      } else {
        setErr(
          typeof detail === "string"
            ? detail
            : "Could not save signup — try again in a moment",
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md" data-testid="waitlist-modal">
        {!done ? (
          <>
            <DialogHeader>
              <DialogTitle data-testid="waitlist-modal-title">
                Reserve your {PLAN_LABEL[plan] || "Pro"} spot
              </DialogTitle>
              <DialogDescription>
                Drop your email — we&rsquo;ll lock in your price the day
                paid plans launch. No card required today.
              </DialogDescription>
            </DialogHeader>
            <form className="space-y-4" onSubmit={handleSubmit}>              {referral && (
                <div
                  data-testid="waitlist-referred-by"
                  className="flex items-center gap-2 rounded-lg border border-indigo-100 bg-indigo-50/70 px-3 py-2 text-xs text-indigo-700"
                >
                  <Users className="h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    Referred by{" "}
                    <span className="font-semibold">{referral}</span>
                    {" · priority access applied"}
                  </span>
                </div>
              )}
              <div>
                <Label htmlFor="wl-email">Email</Label>
                <Input
                  id="wl-email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="waitlist-email-input"
                />
              </div>
              <div>
                <Label htmlFor="wl-name">Name (optional)</Label>
                <Input
                  id="wl-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Investor"
                  data-testid="waitlist-name-input"
                />
              </div>
              {err && (
                <p
                  data-testid="waitlist-error"
                  className="text-xs font-medium text-rose-600"
                >
                  {err}
                </p>
              )}
              <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => handleClose(false)}
                  data-testid="waitlist-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-indigo-600 text-white hover:bg-indigo-700"
                  data-testid="waitlist-submit"
                >
                  <Mail className="mr-1.5 h-4 w-4" />
                  {submitting ? "Saving…" : "Reserve spot"}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : (
          <WaitlistSuccess
            email={signup?.email || email}
            referralCode={signup?.referral_code}
            onClose={() => handleClose(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
